<script setup lang="ts">
import { computed } from 'vue';
import Spinner from '../feedback/Spinner.vue';

/**
 * Icon-only button. Unlike Button (which labels itself with its text slot),
 * an icon carries no accessible name, so `label` is REQUIRED and drives both
 * aria-label and the native tooltip. The icon goes in the default slot.
 */
const props = withDefaults(
  defineProps<{
    label: string;
    /** `title` overrides the tooltip only — for a caveat longer than the accessible
     * name (aria-label stays `label` either way). */
    title?: string;
    /** `primary` is the toolbar's one espresso square (spec §5): the page's main action. */
    variant?: 'ghost' | 'soft' | 'secondary' | 'primary' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    loading?: boolean;
    type?: 'button' | 'submit' | 'reset';
  }>(),
  { variant: 'ghost', size: 'md', disabled: false, loading: false, type: 'button' },
);

const isDisabled = computed(() => props.disabled || props.loading);
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :aria-label="label"
    :title="title ?? label"
    class="lumi-iconbtn"
    :class="[`lumi-iconbtn--${size}`, `lumi-iconbtn--${variant}`]"
  >
    <Spinner v-if="loading" size="sm" />
    <slot v-else />
  </button>
</template>

<style>
/* 36 px square with the button radius (spec §6); the small size fits a 50 px
   table row, the large one is a single prominent action. */
.lumi-iconbtn{display:inline-flex;align-items:center;justify-content:center;border-radius:var(--radius-md);cursor:pointer;transition:background-color var(--dur-fast) var(--ease-standard),border-color var(--dur-fast) var(--ease-standard),color var(--dur-fast) var(--ease-standard);border:1px solid transparent;padding:0;flex-shrink:0;}
.lumi-iconbtn:disabled{opacity:.5;cursor:not-allowed;}
.lumi-iconbtn--sm{width:var(--control-h-sm);height:var(--control-h-sm);}
.lumi-iconbtn--md{width:var(--control-h);height:var(--control-h);}
.lumi-iconbtn--lg{width:var(--control-h-lg);height:var(--control-h-lg);}
.lumi-iconbtn--sm svg{width:16px;height:16px;}
.lumi-iconbtn--md svg{width:18px;height:18px;}
.lumi-iconbtn--lg svg{width:22px;height:22px;}
.lumi-iconbtn--ghost{background:transparent;color:var(--text-secondary);}
.lumi-iconbtn--ghost:hover:not(:disabled){background:var(--surface-wash);color:var(--text-heading);}
.lumi-iconbtn--soft{background:var(--surface-wash);border-color:var(--pill-ring);color:var(--text-heading);}
.lumi-iconbtn--soft:hover:not(:disabled){background:var(--accent-soft-2);border-color:var(--panel-edge);}
.lumi-iconbtn--secondary{background:var(--surface-card);border-color:var(--panel-edge);color:var(--text-heading);}
.lumi-iconbtn--secondary:hover:not(:disabled){background:var(--surface-wash);}
.lumi-iconbtn--primary{background:var(--btn-primary-bg);border-color:var(--btn-primary-bg);color:var(--btn-primary-fg);}
.lumi-iconbtn--primary:hover:not(:disabled){background:var(--accent-hover);border-color:var(--accent-hover);}
.lumi-iconbtn--danger{background:transparent;color:var(--danger);}
.lumi-iconbtn--danger:hover:not(:disabled){background:var(--danger-soft);}
</style>
